package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.GlossaryTermDto
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.localeInfo
import dev.lingvo.shared.textDirection
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

private const val EDITOR_ASSIST_GLOSSARY_HEADING_ID = "editor-assist-glossary-heading"

@Composable
internal fun RelevantGlossary(state: TranslationEditorState, row: KeyRow) {
    val terms = matchingGlossary(state.glossary, row.sourceText())
    if (terms.isEmpty()) return
    Section(
        Modifier
            .attr("data-editor-assist-section", "glossary")
            .attr("aria-labelledby", EDITOR_ASSIST_GLOSSARY_HEADING_ID)
            .toAttrs(),
    ) {
        AssistSectionHeading(
            t("editor.editor_assist_glossary.relevant_glossary", "Relevant glossary"),
            EDITOR_ASSIST_GLOSSARY_HEADING_ID,
        )
        Ul(
            Modifier
                .attr("data-editor-assist-glossary-list", "true")
                .attr("role", "list")
                .toAttrs(),
        ) {
            terms.forEach { term ->
                GlossaryTerm(
                    sourceLocale = state.project?.sourceLocale,
                    targetLocale = state.locale,
                    term = term,
                )
            }
        }
    }
}

@Composable
private fun GlossaryTerm(
    sourceLocale: String?,
    targetLocale: String,
    term: GlossaryTermDto,
) {
    // The language, not the tag. Every other surface in the product prints `de-AT` as "German",
    // and a glossary line reading "No de-AT translation" makes the reader decode a BCP-47 tag in
    // the one panel whose job is telling them what a word must become. Substituted into a literal
    // key rather than interpolated into one: a key built by interpolation is invisible to the
    // catalogue extractor and can never be translated.
    val target = term.translations.firstOrNull { it.locale == targetLocale }?.value
        ?: if (term.doNotTranslate) {
            t("editor.editor_assist_glossary.keep_as_written", "Keep as written")
        } else {
            tf(
                "editor.editor_assist_glossary.no_language_translation",
                "No {language} translation",
                "language" to localeInfo(targetLocale).englishName,
            )
        }
    Li(Modifier.attr("data-editor-glossary-term", "true").toAttrs()) {
        Div(Modifier.attr("data-editor-glossary-header", "true").toAttrs()) {
            Span(
                Modifier
                    .fontWeight(700)
                    .attr("data-editor-glossary-source", "true")
                    .attr("lang", sourceLocale.orEmpty())
                    .attr("dir", textDirection(sourceLocale).html)
                    .fontSize(TypeScale.BodySmall)
                    .toAttrs(),
            ) {
                Text(term.term)
            }
            if (term.doNotTranslate) {
                LBadge(
                    t("editor.editor_assist_glossary.do_not_translate", "Do not translate"),
                    variant = LBadgeVariant.WARNING,
                )
            }
        }
        Div(
            Modifier
                .fontSize(TypeScale.Small)
                .attr("data-editor-glossary-target", "true")
                .attr("lang", targetLocale)
                .attr("dir", textDirection(targetLocale).html)
                .color(ColorMode.current.toSitePalette().textSecondary)
                .toAttrs(),
        ) {
            Text(target)
        }
        term.description?.takeIf(String::isNotBlank)?.let { description ->
            Div(
                Modifier
                    .attr("data-editor-glossary-description", "true")
                    .color(ColorMode.current.toSitePalette().textSecondary)
                    .fontSize(TypeScale.Micro)
                    .toAttrs(),
            ) {
                Text(description)
            }
        }
    }
}

private fun matchingGlossary(terms: List<GlossaryTermDto>, source: String): List<GlossaryTermDto> =
    terms.filter { term ->
        if (term.caseSensitive) source.contains(term.term) else source.contains(term.term, ignoreCase = true)
    }
